/**
 * SoundCloud sign-in: authorisation code + PKCE, and the token store behind it.
 *
 * The fifth copy of the shape built for Google, OneDrive, Dropbox and
 * Spotify - spotifyAuth.ts is the one to read alongside it; the two are
 * deliberately near-identical. What differs is SoundCloud's:
 *
 * - **The token endpoint is our own backend, not SoundCloud.** SoundCloud
 *   treats every client as confidential and wants `client_secret` on the
 *   code exchange and on every refresh. This repo is public, so the secret
 *   lives in the Azure Functions app and `SoundCloudToken` forwards the
 *   request with it added (docs/SOUNDCLOUD.md section 2). It returns
 *   SoundCloud's status and `{ error, error_description }` body unchanged,
 *   so the parsing below is the same as if we had called SoundCloud.
 * - The `Authorization` header prefix is `OAuth`, not `Bearer`. That is
 *   soundCloudApi's business, but it is the kind of thing you look for here.
 * - **Refresh tokens are single-use.** Every refresh returns a new one and
 *   the old one dies, so the one that comes back is always stored. Two tabs
 *   refreshing at once will race and one will lose with `invalid_grant`; on
 *   that, the store is re-read once before giving up, because the winner
 *   has usually written a fresh pair by then.
 * - No scopes. The API has none.
 */

import { backendHost } from '../../settings';
import { shouldUseRedirect } from '../../utilities/platform';
import { hasUserActivation, popupBlockedMessage } from '../../utilities/popupBlocked';
import { soundCloudClientId } from '../../config';

/**
 * SoundCloud allows one redirect URI per app and one app per account
 * (checked 2026-09-09), so there is no localhost sign-in: from
 * https://localhost:3000 the authorize page is blank with a 403 from
 * api-auth.soundcloud.com/allow. SoundCloud is tested on production. The
 * backend picks the secret by whichever client id it is sent.
 */
export const clientID = soundCloudClientId;

const authEndpoint = 'https://secure.soundcloud.com/authorize';

/** Our pass-through, not secure.soundcloud.com/oauth/token - see above. */
export function tokenEndpoint() {
  return backendHost() + '/SoundCloudToken';
}

/**
 * Must match the redirect URI in the SoundCloud app registration byte for
 * byte.
 */
export const redirectPath = '/soundcloud-redirect';

export function redirectUri() {
  return window.location.origin + redirectPath;
}

export interface SoundCloudTokens {
  accessToken: string;
  refreshToken?: string;
  /** Epoch ms. */
  expiresAt?: number;
}

/**
 * The single field the popup posts back to its opener. It carries no token:
 * the popup redeems the code itself and writes the token store, which the
 * opener shares - same origin - so all the opener needs to hear is that it
 * is worth looking again.
 */
export const popupDoneMessage = 'fadilaSoundCloudAuthComplete';

const tokensKey = 'soundCloudAuth';
const pendingKey = 'soundCloudAuthPending';

/** A round trip that has taken longer than this is not one we still expect. */
const pendingLifetimeMs = 10 * 60 * 1000;

interface PendingAuth {
  state: string;
  verifier: string;
  returnTo: string;
  createdAt: number;
}

/* -------------------------------------------------------------------------
 * Token store
 * ---------------------------------------------------------------------- */

export function readTokens(): SoundCloudTokens | undefined {
  try {
    const raw = localStorage.getItem(tokensKey);

    if (!raw) {
      return undefined;
    }

    const parsed = JSON.parse(raw) as SoundCloudTokens;
    return parsed && parsed.accessToken ? parsed : undefined;
  } catch {
    return undefined;
  }
}

export function storeTokens(tokens: SoundCloudTokens) {
  try {
    localStorage.setItem(tokensKey, JSON.stringify(tokens));
  } catch {
    // Private browsing. The token still works for this page's lifetime.
  }
}

export function clearTokens() {
  try {
    localStorage.removeItem(tokensKey);
  } catch {}
}

/* -------------------------------------------------------------------------
 * PKCE
 * ---------------------------------------------------------------------- */

function randomUrlSafe(byteLength: number) {
  const bytes = new Uint8Array(byteLength);

  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  return Array.prototype.map.call(bytes, (b: number) => ('0' + b.toString(16)).slice(-2)).join('');
}

function base64UrlEncode(buffer: ArrayBuffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';

  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * S256, the only method SoundCloud's OAuth 2.1 endpoint takes.
 *
 * crypto.subtle needs a secure context. Everything this app runs in is one -
 * production is HTTPS and the dev server is forced to HTTPS by .env - so this
 * should never throw. If it does, the message says what is missing rather
 * than letting SoundCloud answer with an invalid_request nobody can act on.
 */
async function deriveChallenge(verifier: string) {
  if (!window.crypto || !window.crypto.subtle) {
    throw new Error(
      'SoundCloud sign-in needs Web Crypto, which this browser does not provide here. ' +
        'Open Fadila over HTTPS and try again.',
    );
  }

  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));

  return base64UrlEncode(digest);
}

/* -------------------------------------------------------------------------
 * Starting the flow
 * ---------------------------------------------------------------------- */

function readPending(): PendingAuth | undefined {
  try {
    const raw = localStorage.getItem(pendingKey);

    if (!raw) {
      return undefined;
    }

    const parsed = JSON.parse(raw) as PendingAuth;

    if (!parsed || !parsed.state || Date.now() - parsed.createdAt > pendingLifetimeMs) {
      return undefined;
    }

    return parsed;
  } catch {
    return undefined;
  }
}

function clearPending() {
  try {
    localStorage.removeItem(pendingKey);
  } catch {}
}

/**
 * localStorage rather than sessionStorage, for the same reason gisRedirect
 * gives: iOS may service the cross-origin hop by suspending or even killing
 * the standalone app, and a relaunch starts a fresh session - which would
 * strand the flow at exactly the point it is meant to fix.
 */
function buildAuthUrl() {
  const state = randomUrlSafe(16);
  // 32 bytes hex-encoded is 64 characters, inside the 43-128 range PKCE
  // allows, and hex digits are within its alphabet.
  const verifier = randomUrlSafe(32);

  try {
    localStorage.setItem(
      pendingKey,
      JSON.stringify({
        state,
        verifier,
        // Come back to wherever the user actually was, not a fixed landing page.
        returnTo: window.location.pathname + window.location.search,
        createdAt: Date.now(),
      } as PendingAuth),
    );
  } catch {
    // Private browsing. The trip out still works; the trip back will fail the
    // state check and say so, which beats navigating away silently.
  }

  return deriveChallenge(verifier).then(function buildUrl(challenge) {
    const params = [
      ['client_id', clientID],
      ['redirect_uri', redirectUri()],
      ['response_type', 'code'],
      ['state', state],
      ['code_challenge', challenge],
      ['code_challenge_method', 'S256'],
    ]
      .map((pair) => encodeURIComponent(pair[0]) + '=' + encodeURIComponent(pair[1]))
      .join('&');

    return authEndpoint + '?' + params;
  });
}

/**
 * Whether this device needs the whole page to go to SoundCloud.
 *
 * Exported so callers can tell an authorize() that will return from one that
 * navigates away and never returns at all.
 */
export function willRedirect(allowRedirect?: boolean) {
  return !!allowRedirect && shouldUseRedirect();
}

/**
 * Navigates away. Nothing after this call runs, so the promise a caller is
 * holding never settles - by design, exactly as beginRedirect does for the
 * other providers.
 */
export async function beginRedirect() {
  const authUrl = await buildAuthUrl();

  // assign, not replace: leaves the app in history so back does something sane.
  window.location.assign(authUrl);
}

/**
 * The desktop path. Returns the popup so the caller can watch it close.
 *
 * The popup is opened on about:blank *synchronously*, before the PKCE
 * challenge is derived, and pointed at SoundCloud afterwards. Deriving the
 * challenge is async - crypto.subtle.digest returns a promise - and calling
 * window.open after that await is outside the user gesture, where the popup
 * blocker wins. Opening early and navigating late keeps it inside.
 */
export function beginPopup() {
  // Read before the window.open that is about to fail: afterwards there is no
  // telling whether a click was vouching for this pop-up.
  const hadGesture = hasUserActivation();

  const popup = window.open(
    'about:blank',
    'SoundCloudAuthPopup',
    'width=670,height=800,dialog=yes,dependent=yes,scrollbars=yes,location=yes',
  );

  if (!popup) {
    return Promise.reject(new Error(popupBlockedMessage('SoundCloud', hadGesture)));
  }

  return buildAuthUrl().then(
    function pointPopupAtSoundCloud(authUrl) {
      popup.location.href = authUrl;
      return popup;
    },
    function closePopupOnFailure(err) {
      popup.close();
      throw err;
    },
  );
}

/* -------------------------------------------------------------------------
 * Finishing the flow
 * ---------------------------------------------------------------------- */

export class SoundCloudAuthError extends Error {
  constructor(
    message: string,
    public readonly returnTo: string,
  ) {
    super(message);
    this.name = 'SoundCloudAuthError';
  }
}

/**
 * What the token endpoint said no with. `code` is the OAuth `error` field -
 * `invalid_grant` is the one the refresh path cares about.
 */
export class SoundCloudTokenError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
    public readonly status?: number,
  ) {
    super(message);
    this.name = 'SoundCloudTokenError';
  }
}

async function postForm(body: string[][]) {
  let res: Response;

  try {
    res = await fetch(tokenEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body
        .map((pair) => encodeURIComponent(pair[0]) + '=' + encodeURIComponent(pair[1]))
        .join('&'),
    });
  } catch (err) {
    throw new SoundCloudTokenError(
      'Could not reach the Fadila backend to complete SoundCloud sign-in.',
    );
  }

  const text = await res.text();
  let parsed: any;

  try {
    parsed = JSON.parse(text);
  } catch {
    parsed = undefined;
  }

  if (!res.ok) {
    throw new SoundCloudTokenError(
      (parsed && (parsed.error_description || parsed.error)) ||
        'SoundCloud rejected the token request (' + res.status + ').',
      parsed && parsed.error,
      res.status,
    );
  }

  return parsed;
}

function parseParams(str: string) {
  const out: { [key: string]: string } = {};

  str
    .replace(/^[#?]/, '')
    .split('&')
    .forEach(function readPair(pair) {
      if (!pair) {
        return;
      }

      const idx = pair.indexOf('=');
      const key = decodeURIComponent(idx === -1 ? pair : pair.slice(0, idx));

      out[key] = idx === -1 ? '' : decodeURIComponent(pair.slice(idx + 1).replace(/\+/g, ' '));
    });

  return out;
}

export interface SoundCloudAuthResult {
  returnTo: string;
}

function tokensFromPayload(payload: any, previous?: SoundCloudTokens): SoundCloudTokens {
  return {
    accessToken: payload.access_token,
    // The refresh that comes back is the only live one - the one we sent is
    // spent. Keeping the old one on a missing field is belt and braces; the
    // spec says the field is always there.
    refreshToken: payload.refresh_token || (previous && previous.refreshToken),
    expiresAt: payload.expires_in ? Date.now() + payload.expires_in * 1000 : undefined,
  };
}

/**
 * Exchanges the code SoundCloud handed back and stores the tokens.
 *
 * Runs on /soundcloud-redirect - which is either the popup or, on iOS, the
 * app itself mid-navigation. Doing the exchange in one place rather than two
 * is why the popup posts a bare "done" to its opener instead of posting a
 * code for the opener to redeem.
 */
export async function completeAuth(search: string): Promise<SoundCloudAuthResult> {
  const pending = readPending();
  const returnTo = (pending && pending.returnTo) || '/app';

  clearPending();

  const params = parseParams(search);

  if (params.error) {
    throw new SoundCloudAuthError(params.error_description || params.error, returnTo);
  }

  if (!pending) {
    throw new SoundCloudAuthError(
      'SoundCloud sign-in could not be completed - the request it answers has expired.',
      returnTo,
    );
  }

  if (!params.state || params.state !== pending.state) {
    throw new SoundCloudAuthError('SoundCloud sign-in returned an unexpected response.', returnTo);
  }

  if (!params.code) {
    throw new SoundCloudAuthError('SoundCloud sign-in returned no authorisation code.', returnTo);
  }

  let payload: any;

  try {
    payload = await postForm([
      ['grant_type', 'authorization_code'],
      ['code', params.code],
      ['redirect_uri', redirectUri()],
      ['client_id', clientID],
      ['code_verifier', pending.verifier],
    ]);
  } catch (err) {
    throw new SoundCloudAuthError((err as Error).message, returnTo);
  }

  if (!payload || !payload.access_token) {
    throw new SoundCloudAuthError('SoundCloud returned no access token.', returnTo);
  }

  storeTokens(tokensFromPayload(payload));

  return { returnTo };
}

/* -------------------------------------------------------------------------
 * Keeping it alive
 * ---------------------------------------------------------------------- */

/** Renew this far ahead of expiry, so a request in flight does not age out. */
const renewMarginMs = 5 * 60 * 1000;

/**
 * In-flight refresh, shared, so concurrent API calls do not each spend the
 * single-use refresh token and leave all but one holding a dead pair.
 */
let refreshInFlight: Promise<SoundCloudTokens> | undefined;

async function refresh(tokens: SoundCloudTokens): Promise<SoundCloudTokens> {
  let payload: any;

  try {
    payload = await postForm([
      ['grant_type', 'refresh_token'],
      ['refresh_token', tokens.refreshToken!],
      ['client_id', clientID],
    ]);
  } catch (err) {
    if (err instanceof SoundCloudTokenError && err.code === 'invalid_grant') {
      // The refresh token we sent was already spent. The usual reason is
      // another tab of this app refreshing a moment earlier - in which case
      // it has stored the pair it got, and that pair is ours to use.
      const latest = readTokens();

      if (
        latest &&
        latest.accessToken !== tokens.accessToken &&
        latest.refreshToken &&
        latest.refreshToken !== tokens.refreshToken
      ) {
        return latest;
      }

      // Nobody else won it either: the session is over.
      clearTokens();
    }

    throw err;
  }

  if (!payload || !payload.access_token) {
    throw new Error('SoundCloud returned no access token when renewing.');
  }

  const renewed = tokensFromPayload(payload, tokens);

  storeTokens(renewed);

  return renewed;
}

/**
 * The current access token, renewed first if it is close to expiring.
 *
 * Returns undefined when there is nothing stored and nothing to renew with -
 * that is "not signed in", not an error.
 */
export function getFreshAccessToken(): Promise<string | undefined> {
  const tokens = readTokens();

  if (!tokens) {
    return Promise.resolve(undefined);
  }

  const stillGood = tokens.expiresAt === undefined || tokens.expiresAt - Date.now() > renewMarginMs;

  if (stillGood || !tokens.refreshToken) {
    return Promise.resolve(tokens.accessToken);
  }

  if (!refreshInFlight) {
    refreshInFlight = refresh(tokens).finally(function clearInFlight() {
      refreshInFlight = undefined;
    });
  }

  return refreshInFlight.then(
    (renewed) => renewed.accessToken,
    function useWhateverIsStoredAndLet401Decide() {
      // A failed renewal is not fatal here. If the store still holds a token,
      // the request happens with it and fails with a 401, which
      // soundCloudService's error handler turns into a logout - one place
      // that decides that. If refresh() cleared the store, this is a plain
      // "not signed in".
      const remaining = readTokens();
      return remaining && remaining.accessToken;
    },
  );
}
